import { Component, OnDestroy, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { _DialogComingSoonComponent } from './components/dialog-coming-soon.component';
import { _ToastComponent } from './components/toast.component';
import { _DialogLoadingComponent } from './components/dialog-loading.component';
import { _DialogImagePreviewComponent } from './components/dialog-image-preview.component';
import { AuthService, Profile } from 'src/services/auth.service';
import { RestService } from 'src/services/rest.service';
import { IconModule } from 'src/components/icon/icon.module';
import { ToastService } from 'src/services/toast.service';
import { GlobalService } from 'src/services/global.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputOtpModule } from 'primeng/inputotp';
import { ButtonComponent } from 'src/components/button/button.component';
import { AutoFocusModule } from 'primeng/autofocus';
import { filter, lastValueFrom, Subscription } from 'rxjs';
import { PermissionService } from 'src/services/permission.service';
import { NavigationEnd, Router } from '@angular/router';
import { MenuService } from 'src/services/menu.service';
import { PUBLIC_ROUTE } from 'src/definitions/global.definition';
import { SseService } from 'src/services/sse.service';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { LayoutAuthComponent } from 'src/components/layout/layout-auth.component';
import { GetThumbnailResponse } from 'src/definitions/rest.definition';
import { ForgotPinPage } from './forgot-pin/forgot-pin.page';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [
    IonApp,
    IonRouterOutlet,
    InputOtpModule,
    FormsModule,
    CommonModule,
    AutoFocusModule,
    ForgotPinPage,
    IconModule,
    ButtonComponent,
    LayoutAuthComponent,
    _ToastComponent,
    _DialogLoadingComponent,
    _DialogComingSoonComponent,
    _DialogImagePreviewComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  sessionChecking: boolean = true;

  insertPinRequired: 'login' | 'refresh' | null = null;
  insertPinRequired$?: Subscription;

  pin: string = '';
  pinLength: number = 6;
  pinError: string = '';
  pinType: 'password' | 'text' = 'password';
  pinSubmitting: boolean = false;

  thumbnail: GetThumbnailResponse['data'] = {
    name: '',
    thumbnail_url: '',
  };
  thumbnail$?: Subscription;

  mode: 'insert-pin' | 'forget-pin' = 'insert-pin';

  profile?: Profile | null;
  profile$?: Subscription;

  constructor(
    private router: Router,
    private sseService: SseService,
    private menuService: MenuService,
    private restService: RestService,
    private globalService: GlobalService,
    private permissionService: PermissionService,
    protected authService: AuthService,
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (!PUBLIC_ROUTE.includes(event.urlAfterRedirects)) {
          this.__initSession();
          return;
        }

        if (this.profile) this.sseService.connect(this.profile.id);

        this.sessionChecking = false;
      });
  }

  ngOnInit(): void {
    this.__subscribe();
  }

  ngOnDestroy(): void {
    this.__unsubscribe();
  }

  onPaste(event: ClipboardEvent) {
    const value = event.clipboardData?.getData('text')?.trim();
    if (!Number(value)) return;
    this.pin = value?.slice(0, this.pinLength) ?? '';
  }

  onTogglePinType() {
    this.pinType = this.pinType === 'password' ? 'text' : 'password';
  }

  async onSubmit() {
    if (this.insertPinRequired === 'login') {
      this.__submitLogin();
      return;
    }

    this.__submitRefreshPin();
  }

  private async __submitLogin() {
    if (this.pinSubmitting) return;

    try {
      this.pinSubmitting = true;

      const payload = { pin: this.pin };
      const response = await lastValueFrom(this.restService.login(payload));

      this.authService.createSession(response.data);

      this.permissionService.applyStoredPermission();
      const redirectTo = await this.menuService.getAfterLoginPage();
      if (redirectTo) this.router.navigateByUrl(redirectTo, { replaceUrl: true });

      setTimeout(() => {
        this.authService.insertPinRequired$.next(null);
        this.sseService.connect(response.data.user.id);
      }, 300);
    } catch (error) {
      if ((error as HttpErrorResponse).status === HttpStatusCode.Unauthorized) {
        this.authService.logout();
        return;
      }
      this.globalService.errorHandler(error, 'Gagal masuk ke sistem');
    } finally {
      this.pinSubmitting = false;
    }
  }

  private async __submitRefreshPin() {
    if (this.pinSubmitting) return;

    try {
      this.pinSubmitting = true;

      const refreshToken = this.authService.token$.getValue()?.refresh_token ?? '';
      const payload = {
        pin: this.pin,
        refresh_token: refreshToken,
      };
      const response = await lastValueFrom(this.restService.refreshPin(payload));

      this.authService.setToken({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
      });

      setTimeout(() => {
        this.authService.insertPinRequired$.next(null);
        const profile = this.authService.profile$.getValue();
        if (profile) this.sseService.connect(profile.id);
      }, 300);
    } catch (error) {
      if ((error as HttpErrorResponse).status === HttpStatusCode.Unauthorized) {
        this.authService.logout();
        return;
      }
      this.globalService.errorHandler(error, 'Gagal masuk ke sistem');
    } finally {
      this.pinSubmitting = false;
    }
  }

  private async __initSession() {
    try {
      this.sessionChecking = true;

      const session = await this.authService.initSession();
      const hasStoredSession = Boolean(session);
      if (hasStoredSession && session) {
        this.sessionChecking = false;
        this.sseService.connect(session.user.id);
        return;
      }

      this.__checkNeedInputLoginPin();
    } catch (error) {
      this.globalService.errorHandler(error, 'Gagal memeriksa butun input pin atau tidak');
    } finally {
      this.sessionChecking = false;
    }
  }

  private async __checkNeedInputLoginPin() {
    try {
      await this.authService.checkHasCookieSession();
    } catch (error) {
      this.globalService.errorHandler(error, 'Gagal memeriksa butun input pin atau tidak');
    }
  }

  private __subscribe() {
    this.insertPinRequired$ = this.authService.insertPinRequired$.subscribe((type) => {
      this.insertPinRequired = type;
    });

    this.thumbnail$ = this.globalService.thumbnail$.subscribe((thumbnail) => {
      this.thumbnail = thumbnail;
    });

    this.profile$ = this.authService.profile$.subscribe((profile) => {
      this.profile = profile;
    });
  }

  private __unsubscribe() {
    this.insertPinRequired$?.unsubscribe();
    this.thumbnail$?.unsubscribe();
    this.profile$?.unsubscribe();
  }
}
